import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import { DataAPIContext } from '../../api';
import { previewCopy, withChange } from './beforeAfter';
import {
  KIND_LABELS, createMockApi, describeTarget, fillTemplate, reminderGapsApi, reminderLabel,
} from './reminderGapsApi';

/*
 * Reminder gaps: questions guests asked after receiving the operator's reminders. The operator
 * fills in the facts (or edits the wording) and requests a change; TourOptima ops makes it and
 * marks it live. Ops (staff accounts) also see Assign / Mark as live / Ask on requested items.
 * Mockup: https://claude.ai/artifact/H9tvGuhADzzvKqdqmWgBxc
 */
const STATUS_TABS = [
  { key: 'open', label: 'To review' },
  { key: 'requested', label: 'Requested' },
  { key: 'live', label: 'Live' },
  { key: 'ignored', label: 'Not needed' },
];
const KIND_COLORS = { product: 'primary', template: 'warning', app: 'default' };

// For a card in an early message (post-booking, 7-day): what the same guests got before their tour
// (backend evidence.later, over guests whose tour is over). "answered" -- a later reminder answers them,
// they asked too early; "short_notice" -- nothing came, and most booked two days or less before the
// tour; "no_pre_tour" -- nothing came although most booked earlier. Chat copies only.
export const laterNote = (gap) => {
  const later = gap?.evidence?.later;
  if (!later?.verdict) return null;
  const here = reminderLabel(previewCopy(gap)?.kind || gap?.suggestion?.position?.reminder);
  if (later.verdict === 'answered') {
    return {
      severity: 'info',
      text: `Your ${reminderLabel(later.kind)} answers this, but guests asked before it arrived: ${later.pre_tour} of the `
        + `${later.known} who asked got it later. The change brings it forward into the ${here}.`,
    };
  }
  if (later.verdict === 'short_notice') {
    return {
      severity: 'info',
      text: `Most guests here booked at short notice: ${later.short} of the ${later.known} who asked booked two days `
        + `or less before the tour, so the ${here} is the only one they get before it. Add this to the ${here}, `
        + 'before any promotion.',
    };
  }
  if (later.verdict === 'no_pre_tour') {
    const got = later.pre_tour ? `only ${later.pre_tour} of the ${later.known}` : `none of the ${later.known}`;
    const early = later.known - later.pre_tour - later.short;
    const ahead = Number.isFinite(early)
      ? `, though ${early === later.known ? `all ${early}` : early} booked more than two days ahead`
      : '';
    return {
      severity: 'warning',
      text: `Guests get no reminder in chat between the ${here} and the tour: ${got} who asked got one${ahead}. `
        + `Consider a reminder 24–48 hours before the tour; until then, add this to the ${here}, before any promotion.`,
    };
  }
  return null;
};

const topAfter = (after) => {
  const entries = Object.entries(after || {}).sort((a, b) => b[1] - a[1]);
  return entries.length ? reminderLabel(entries[0][0]) : '—';
};

// A template finding combines several tours: its rate is over those tours, not every tour the
// template goes to, so say so.
const rateLabel = (gap) => {
  const n = gap?.evidence?.tours?.length || 0;
  return !gap?.tour && n > 1 ? `per 100 bookings on ${n} tours` : 'per 100 bookings';
};

function Stat({ value, label }) {
  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, px: 1.75, py: 1.25, flex: '1 1 140px' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value ?? '—'}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}

// Before and after: the reminder a recent guest received, next to the same message with `text` in
// it (see beforeAfter.js). Collapsed, like What guests received.
function BeforeAfter({ gap, text }) {
  const copy = previewCopy(gap);
  const change = copy && withChange(copy.text, text, gap);
  if (!change) return null;
  const box = { bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1, height: '100%' };
  const prose = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
  const marked = (color) => (theme) => ({ bgcolor: alpha(theme.palette[color].main, 0.22), color: 'inherit', borderRadius: 0.5, px: 0.25 });
  return (
    <Accordion disableGutters variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Before and after</Typography>
          <Typography variant="body2" color="text.secondary">This change in the {reminderLabel(copy.kind)} guests received</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Now</Typography>
              <Box sx={box}>
                <Typography variant="body2" sx={prose}>
                  {change.removed ? (
                    <>
                      {change.before}
                      <Box component="mark" data-testid="replaced" sx={marked('warning')}>{change.removed}</Box>
                      {change.after}
                    </>
                  ) : copy.text}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">With this change</Typography>
              <Box sx={box}>
                <Typography variant="body2" sx={prose}>
                  {change.before}
                  <Box component="mark" data-testid="added" sx={marked('success')}>{change.added}</Box>
                  {change.after}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {change.removed ? 'The new wording replaces the highlighted sentence. ' : ''}
            A preview on a recent guest’s copy. The final wording and position are set when the reminder is edited.
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// What guests received: a recent guest's chat copy of the reminders the questions followed (backend
// evidence.received; their name and personal links are hidden there). Collapsed: it is reference
// for writing the change, not the point of the card.
function Received({ gap }) {
  const items = gap.evidence?.received || [];
  if (!items.length) return null;
  // A template finding shows its lead tour's copy.
  const tour = gap.tour ? null : gap.evidence?.tours?.[0]?.name;
  const label = (kind) => {
    const text = reminderLabel(kind);
    return text.charAt(0).toUpperCase() + text.slice(1);
  };
  return (
    <Accordion disableGutters variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">What guests received</Typography>
          <Typography variant="body2" color="text.secondary">{items.map((r) => label(r.kind)).join(' · ')}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {items.map((r) => (
            <Stack key={r.kind} spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{label(r.kind)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Sent {new Date(r.ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                {tour ? ` on ${tour}` : ''}
                {r.template ? ` · template “${r.template}”` : ''}
              </Typography>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.text}</Typography>
              </Box>
            </Stack>
          ))}
          <Typography variant="caption" color="text.secondary">
            One recent guest’s copy, as it appeared in their chat. Their name and personal links are hidden.
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function Thread({ thread }) {
  if (!thread?.length) return null;
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Messages</Typography>
      {thread.map((m, i) => (
        <Box key={i} sx={{ bgcolor: m.who === 'ops' ? 'info.50' : 'action.hover', border: 1, borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1 }}>
          <Typography variant="caption" color="text.secondary">{m.who === 'ops' ? 'TourOptima operations' : 'Your team'}</Typography>
          <Typography variant="body2">{m.text}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

function OpenForm({ gap, busy, onRequest, onIgnore }) {
  const blanks = gap.suggestion?.blanks || [];
  const [answers, setAnswers] = useState(() => Object.fromEntries(blanks.map((b, i) => [i + 1, b.default || ''])));
  const [custom, setCustom] = useState(null);
  const [editing, setEditing] = useState(false);
  const [allLanguages, setAllLanguages] = useState(true);
  const generated = fillTemplate(gap.suggestion, answers);
  const text = custom ?? generated;
  const isProduct = gap.kind === 'product';
  const lang = gap.target?.lang;

  const submit = () => onRequest({
    answers,
    text,
    edited: custom !== null && custom !== generated,
    languages: allLanguages ? ['all'] : (lang ? [lang] : []),
  });

  return (
    <Stack spacing={2} sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      {blanks.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Tell us the details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            {blanks.map((b, i) => (b.options?.length ? (
              <TextField
                key={i}
                select
                size="small"
                label={b.label}
                value={answers[i + 1] || ''}
                onChange={(e) => { setAnswers((a) => ({ ...a, [i + 1]: e.target.value })); setCustom(null); }}
              >
                {b.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            ) : (
              <TextField
                key={i}
                size="small"
                label={b.label}
                value={answers[i + 1] || ''}
                onChange={(e) => { setAnswers((a) => ({ ...a, [i + 1]: e.target.value })); setCustom(null); }}
              />
            )))}
          </Box>
        </>
      )}
      {gap.suggestion?.template ? (
        <Stack spacing={0.75}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">
              {editing ? 'Edit the wording' : (custom !== null ? 'Your wording (preview)' : 'Suggested addition (preview)')}
            </Typography>
            <Button size="small" onClick={() => { if (!editing && custom === null) setCustom(generated); setEditing(!editing); }}>
              {editing ? 'Done editing' : (custom !== null ? 'Edit again' : 'Edit wording')}
            </Button>
          </Stack>
          {editing ? (
            <TextField
              multiline
              minRows={3}
              value={text}
              onChange={(e) => setCustom(e.target.value)}
              helperText="Our operations team reviews your wording before it goes into the reminder."
            />
          ) : (
            <Box sx={{ bgcolor: 'success.50', border: 1, borderColor: 'success.light', borderRadius: 1.5, p: 1.5 }}>
              <Typography>{text}</Typography>
            </Box>
          )}
        </Stack>
      ) : null}
      <BeforeAfter gap={gap} text={text} />
      {isProduct && (
        <FormControlLabel
          control={<Checkbox checked={allLanguages} onChange={(e) => setAllLanguages(e.target.checked)} />}
          label="Also apply to the other language versions of this tour"
        />
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="contained" disabled={busy || (!text && gap.kind !== 'app')} onClick={submit}>
          {isProduct ? 'Request this change' : 'Send to TourOptima operations'}
        </Button>
        <Button variant="outlined" color="inherit" disabled={busy} onClick={onIgnore}>Not needed</Button>
      </Stack>
    </Stack>
  );
}

// Where a request stands, in the words of whoever is reading: the operator is told ops has it;
// ops are told who asked and what is left to do.
const requestStatus = (gap, ops) => {
  if (!ops) {
    const who = gap.status === 'in_progress' ? 'TourOptima operations is making this change.' : 'Sent to TourOptima operations.';
    return `${who} We’ll move this to Live when guests start receiving it.`;
  }
  if (gap.status === 'in_progress') {
    const assignee = gap.assigned_to?.name ? `, assigned to ${gap.assigned_to.name}` : '';
    return `In progress${assignee}. Mark it as live once guests get the new wording.`;
  }
  const on = gap.requested_ts ? ` on ${new Date(gap.requested_ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : '';
  return `Requested by ${gap.requested_by?.name || 'the operator'}${on}, not assigned yet: assign it to yourself, make the change, then mark it as live.`;
};

function RequestedView({ gap, ops, busy, onReply, onAssign, onMarkLive, onAsk }) {
  const [message, setMessage] = useState('');
  const send = (fn) => { if (message.trim()) { fn(message.trim()); setMessage(''); } };
  return (
    <Stack spacing={2} sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <Alert severity="info">{requestStatus(gap, ops)}</Alert>
      {gap.requested_text && (
        <Typography variant="body2"><b>Requested text:</b> {gap.requested_text}</Typography>
      )}
      <BeforeAfter gap={gap} text={gap.requested_text} />
      {gap.edited && (
        <Typography variant="body2" color={ops ? 'warning.dark' : 'text.secondary'}>
          {ops ? 'Edited by the operator: check the wording, tone and facts before applying it.' : 'You edited the wording.'}
        </Typography>
      )}
      {ops && (
        <Typography variant="body2" color="text.secondary">
          {describeTarget(gap)}{gap.languages?.length ? ` · languages: ${gap.languages.join(', ')}` : ''}
          {gap.suggestion?.position?.text ? (
            <Box component="span" sx={{ display: 'block' }}>
              {gap.suggestion.position.type === 'replace' ? 'Replaces' : 'Add it after'}, in the {reminderLabel(gap.suggestion.position.reminder)}: “{gap.suggestion.position.text}”
            </Box>
          ) : null}
        </Typography>
      )}
      <Thread thread={gap.thread} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField
          size="small"
          fullWidth
          label={ops ? 'Ask the operator a question' : 'Reply to TourOptima operations'}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Button variant="outlined" disabled={busy || !message.trim()} onClick={() => send(ops ? onAsk : onReply)}>Send</Button>
      </Stack>
      {ops && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {gap.status === 'requested' && <Button variant="outlined" disabled={busy} onClick={onAssign}>Assign to me</Button>}
          <Button variant="contained" disabled={busy} onClick={onMarkLive}>Mark as live</Button>
        </Stack>
      )}
    </Stack>
  );
}

function LiveView({ gap }) {
  const langs = Object.entries(gap.verify || {});
  return (
    <Stack spacing={1.5} sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip color="success" size="small" label={`Live${gap.live_marked_ts ? ` since ${new Date(gap.live_marked_ts).toLocaleDateString()}` : ''}`} />
        {langs.map(([lang, v]) => (
          <Chip key={lang} size="small" variant="outlined" color={v?.seen ? 'success' : 'warning'} label={v?.seen ? `${lang} ✓` : `${lang}: not seen yet`} />
        ))}
      </Stack>
      {gap.requested_text && <Typography variant="body2"><b>Now in the reminder:</b> {gap.requested_text}</Typography>}
      <BeforeAfter gap={gap} text={gap.requested_text} />
      <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
        <Typography sx={{ fontWeight: 700 }}>Measuring</Typography>
        <Typography variant="body2" color="text.secondary">
          We compare questions on this topic per 100 bookings with the 30 days before the change, once guests have been
          receiving it for 14 days and 200 bookings.
        </Typography>
      </Box>
    </Stack>
  );
}

export default function ReminderGapsTab({ licenseeId, live, onOpenCountChange }) {
  const dataAPI = useContext(DataAPIContext);
  // Sample data unless the operator's reminder_gaps_enabled lets this user in (see reminderGapsApi.js).
  const api = useMemo(() => (live ? reminderGapsApi(dataAPI, licenseeId) : createMockApi()), [live, dataAPI, licenseeId]);
  // "View as TourOptima ops": in the preview, anyone can switch it; with real data only TourOptima
  // staff (summary.ops) get it. Off to start (the operator's screen: To review looks the same either
  // way); switching it on opens Requested, where the ops controls are. It changes the screen only --
  // the backend still treats staff actions as ops.
  const [viewAsOps, setViewAsOps] = useState(false);
  // The tab before switching to the ops view, to return to when switching back.
  const [tabBeforeOps, setTabBeforeOps] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('open');
  const [summary, setSummary] = useState({ counts: {}, ops: false });
  const [gaps, setGaps] = useState(null);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let active = true;
    setGaps(null);
    setError(null);
    Promise.all([api.summary(), api.list(tab)])
      .then(([s, rows]) => {
        if (!active) return;
        setSummary(s || { counts: {}, ops: false });
        if (onOpenCountChange) onOpenCountChange(s?.counts?.open ?? null);
        setGaps(rows || []);
        setSelId((cur) => ((rows || []).some((g) => g.id === cur) ? cur : rows?.[0]?.id ?? null));
      })
      .catch((e) => { if (active) setError(e?.message || 'Couldn’t load reminder gaps.'); });
    return () => { active = false; };
  }, [api, tab, onOpenCountChange]);
  useEffect(load, [load]);

  const sel = (gaps || []).find((g) => g.id === selId) || null;
  const opsView = !!summary.ops && viewAsOps;
  const toggleOps = (on) => {
    setViewAsOps(on);
    if (api.setOps) api.setOps(on);
    // The ops controls live on requested items: go there when switching to the ops view, and back
    // to where you were when switching it off.
    if (on) {
      setTabBeforeOps(tab);
      if (tab !== 'requested') setTab('requested'); else load();
    } else {
      const back = tabBeforeOps || 'open';
      setTabBeforeOps(null);
      if (back !== tab) setTab(back); else load();
    }
  };
  const opsSwitch = (
    <FormControlLabel
      sx={{ mr: 1 }}
      control={<Switch size="small" checked={viewAsOps}onChange={(e) => toggleOps(e.target.checked)} />}
      label="View as TourOptima ops"
    />
  );
  const opsNote = opsView && (
    <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
      Viewing as TourOptima ops: open a request to assign it, mark it live or ask the operator a question.
    </Box>
  );

  const run = (fn, done) => {
    setBusy(true);
    fn()
      .then((g) => { enqueueSnackbar(done, { variant: 'success' }); setGaps((rows) => (rows || []).map((r) => (r.id === g.id ? g : r))); load(); })
      .catch((e) => enqueueSnackbar(`Couldn’t save that: ${e?.message || 'please try again'}`, { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  return (
    <Stack spacing={2}>
      {!live && (
        <Alert severity="info" sx={{ borderRadius: '12px' }} action={opsSwitch}>
          Preview with sample data. Nothing you do here is saved.
          {opsNote}
        </Alert>
      )}
      {live && summary.ops && (
        <Alert severity="warning" icon={false} sx={{ borderRadius: '12px' }} action={opsSwitch}>
          {opsView ? 'TourOptima staff only: switch off to see this tab the way the operator does.' : 'TourOptima staff only: you’re seeing this tab the way the operator does. Switch on for the ops view.'}
          {opsNote}
        </Alert>
      )}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Questions your reminders could answer</Typography>
        <Typography variant="body2" color="text.secondary">
          Guests asked these after receiving a reminder. Fill in the details and request a change: our operations team
          updates the reminder for you, and we’ll show whether the questions drop.
        </Typography>
      </Box>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" aria-label="Reminder gap status">
        {STATUS_TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={`${t.label} (${summary.counts?.[t.key] ?? 0})`} />
        ))}
      </Tabs>
      {error && <Alert severity="error">{error}</Alert>}
      {gaps === null && !error && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
      {gaps && gaps.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderStyle: 'dashed' }}>
          <Typography color="text.secondary">
            {opsView && tab === 'requested'
              ? 'No requests yet. When the operator sends a change it appears here (and in an email to operations@) for you to assign, mark live or ask about.'
              : 'Nothing here yet.'}
          </Typography>
        </Paper>
      )}
      {gaps && gaps.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          <Paper variant="outlined" sx={{ flex: '1 1 320px', maxWidth: { md: 400 }, borderRadius: 2 }}>
            <List disablePadding>
              {gaps.map((g) => (
                <ListItemButton key={g.id} selected={g.id === selId} onClick={() => setSelId(g.id)} divider alignItems="flex-start">
                  <Stack spacing={0.5} sx={{ width: '100%' }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" color={KIND_COLORS[g.kind] || 'default'} label={KIND_LABELS[g.kind] || g.kind} />
                      <Typography variant="caption" color="text.secondary">{g.tour?.name || 'All tours'}</Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{g.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {g.evidence?.questions ?? 0} questions{g.evidence?.per_100 != null ? ` · ${g.evidence.per_100} ${rateLabel(g)}` : ''}
                    </Typography>
                  </Stack>
                </ListItemButton>
              ))}
            </List>
          </Paper>
          {sel && (
            <Paper variant="outlined" sx={{ flex: '999 1 520px', minWidth: 0, p: 2.5, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" color={KIND_COLORS[sel.kind] || 'default'} label={KIND_LABELS[sel.kind] || sel.kind} />
                    <Typography variant="body2" color="text.secondary">{sel.tour?.name || 'All tours'} · {sel.topic_label}</Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{sel.title}</Typography>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <Stat value={sel.evidence?.questions} label="guests asked" />
                  <Stat value={sel.evidence?.per_100} label={rateLabel(sel)} />
                  <Stat value={topAfter(sel.evidence?.after)} label="most asked after" />
                </Box>
                {sel.evidence?.tours?.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Across {sel.evidence.tours.length} tours, most on {sel.evidence.tours.slice(0, 3).map((t) => t.name).join(', ')}.
                  </Typography>
                )}
                <Stack spacing={1}>
                  <Typography variant="subtitle2">What guests asked</Typography>
                  {(sel.evidence?.samples || []).slice(0, 4).map((q, i) => {
                    // The conversation the question came from (backend evidence.sample_conversations).
                    const conv = sel.evidence?.sample_conversations?.[i];
                    return (
                      <Stack
                        key={i}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1 }}
                      >
                        <Typography variant="body2">“{q}”</Typography>
                        {conv && (
                          <Link
                            href={`/chat/${conv.channel}/conversations/${conv.name}/interactivechat`}
                            target="_blank"
                            rel="noopener"
                            underline="hover"
                            variant="body2"
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            Open conversation
                          </Link>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2">What guests need</Typography>
                  <Typography variant="body2">{sel.what_guests_need || '—'}</Typography>
                  {sel.current_text ? (
                    <Typography variant="body2" color="text.secondary"><b>The reminder says today:</b> {sel.current_text}</Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">{describeTarget(sel)}</Typography>
                </Stack>
                {laterNote(sel) && (
                  <Alert severity={laterNote(sel).severity} sx={{ borderRadius: 2 }}>{laterNote(sel).text}</Alert>
                )}
                <Received gap={sel} />
                {sel.status === 'open' && (
                  <OpenForm
                    key={sel.id}
                    gap={sel}
                    busy={busy}
                    onRequest={(data) => run(() => api.request(sel.id, data), 'Sent to TourOptima operations')}
                    onIgnore={() => run(() => api.ignore(sel.id), 'Marked as not needed')}
                  />
                )}
                {(sel.status === 'requested' || sel.status === 'in_progress') && (
                  <RequestedView
                    gap={sel}
                    ops={opsView}
                    busy={busy}
                    onReply={(t) => run(() => api.reply(sel.id, t), 'Sent')}
                    onAsk={(t) => run(() => api.ask(sel.id, t), 'Question sent')}
                    onAssign={() => run(() => api.assign(sel.id), 'Assigned to you')}
                    onMarkLive={() => run(() => api.markLive(sel.id), 'Marked as live')}
                  />
                )}
                {sel.status === 'live' && <LiveView gap={sel} />}
                {sel.status === 'ignored' && (
                  <Box>
                    <Button variant="outlined" disabled={busy} onClick={() => run(() => api.reopen(sel.id), 'Moved back to review')}>
                      Move back to review
                    </Button>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </Box>
      )}
    </Stack>
  );
}
